import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Baby, Calendar, MessageCircle, BookOpen, Plus } from "lucide-react";

export default function ParentDashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [children, setChildren] = useState([]);
  const [appts, setAppts] = useState([]);
  const [purchases, setPurchases] = useState([]);

  useEffect(() => {
    if (!user) { nav("/login"); return; }
    api.get("/children").then((r) => setChildren(r.data));
    api.get("/appointments").then((r) => setAppts(r.data));
    api.get("/my/purchases").then((r) => setPurchases(r.data));
  }, [user, nav]);

  if (!user) return null;

  const upcoming = appts.filter((a) => ["confirmed","pending_payment","payment_review"].includes(a.status));
  const past = appts.filter((a) => ["completed","cancelled"].includes(a.status));

  return (
    <div className="max-w-6xl mx-auto px-4 py-8" data-testid="parent-dashboard">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="overline mb-1">Welcome</div>
          <h1 className="font-display font-black text-3xl">Hi {user.name?.split(" ")[0]} 👋</h1>
        </div>
        <Link to="/book" className="btn-primary text-sm"><Plus size={16}/> New Consultation</Link>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-6">
        <div className="card-soft">
          <Baby color="#2D7A78" strokeWidth={1.7}/><div className="text-3xl font-display font-black mt-2">{children.length}</div>
          <div className="text-sm text-[#5A6D6D]">Child profiles</div>
        </div>
        <div className="card-soft">
          <Calendar color="#2D7A78" strokeWidth={1.7}/><div className="text-3xl font-display font-black mt-2">{appts.length}</div>
          <div className="text-sm text-[#5A6D6D]">Consultations</div>
        </div>
        <div className="card-soft">
          <BookOpen color="#2D7A78" strokeWidth={1.7}/><div className="text-3xl font-display font-black mt-2">{purchases.length}</div>
          <div className="text-sm text-[#5A6D6D]">E-Books</div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="card-soft">
          <div className="flex items-center justify-between mb-3"><h3 className="font-display font-bold">Upcoming</h3><Link to="/book" className="text-xs text-[#2D7A78] font-semibold">+ Book</Link></div>
          {upcoming.length === 0 && <div className="text-sm text-[#5A6D6D]">No upcoming consultations.</div>}
          {upcoming.map((a) => (
            <div key={a.id} className="border-t border-[#E5EBEB] py-3 flex items-center justify-between" data-testid={`appt-${a.id}`}>
              <div>
                <div className="font-semibold text-sm">{a.concern_category} — {a.consultation_type}</div>
                <div className="text-xs text-[#5A6D6D]">{new Date(a.preferred_datetime).toLocaleString()} · <span className="chip">{a.status}</span></div>
              </div>
              <div className="flex gap-1">
                <Link to={`/chat/${a.id}`} className="btn-outline text-xs px-3 py-1" data-testid={`open-chat-${a.id}`}>Chat</Link>
                {a.meeting_link && a.status==="confirmed" && <a href={a.meeting_link} target="_blank" rel="noreferrer" className="btn-primary text-xs px-3 py-1">Join</a>}
              </div>
            </div>
          ))}
        </div>
        <div className="card-soft">
          <h3 className="font-display font-bold mb-3">Past consultations</h3>
          {past.length === 0 && <div className="text-sm text-[#5A6D6D]">No past consultations.</div>}
          {past.map((a) => (
            <div key={a.id} className="border-t border-[#E5EBEB] py-3">
              <div className="font-semibold text-sm">{a.concern_category}</div>
              <div className="text-xs text-[#5A6D6D]">{new Date(a.preferred_datetime).toLocaleDateString()} · <span className="chip">{a.status}</span></div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-8">
        <h3 className="font-display font-bold mb-3">My children</h3>
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
          {children.map((c) => (
            <div key={c.id} className="card-soft" data-testid={`child-card-${c.id}`}>
              <div className="flex items-center gap-2"><Baby color="#2D7A78" size={20}/><div className="font-semibold">{c.name}</div></div>
              <div className="text-xs text-[#5A6D6D] mt-1">{c.sex} · DOB {c.dob}</div>
              {c.current_weight_kg && <div className="text-xs text-[#5A6D6D]">Weight: {c.current_weight_kg} kg</div>}
              <Link to="/trackers" className="text-xs text-[#2D7A78] font-semibold mt-2 inline-block">Open trackers →</Link>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
